import React, { useRef, useState } from 'react';
import ReactModal from 'react-modal';
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'common/components/modal/Modal';
import { Header, Label, Text } from 'common/components/texts/Texts';
import { useTranslation } from 'react-i18next';
import Button from 'common/components/button/Button';
import CheckInput from 'common/components/checkInput/CheckInput';
import ExpressionInput, { ExpressionInputRef } from 'common/components/expressionButton/ExpressionInput';
import ExpressionSystem, { ExpressionParameter, ExpressionResult } from 'common/components/expressionButton/ExpressionSystem';
import FunctionReferenceModal from 'common/components/expressionButton/FunctionReferenceModal';
import Storage from 'common/services/Storage';
import { BODY_TOOLTIP } from 'Config';
import { IoCopyOutline } from 'react-icons/io5';
import { FaBook, FaInfoCircle, FaPaste } from 'react-icons/fa';
import styles from './DiscountSectionsModal.module.scss';

export interface DiscountSection {
    key: string;
    designation: string;
    isEligibleForDiscount: boolean;
    hasDiscountApplied: boolean;
    hasInconsistentFormulas: boolean;
}

type Props = {
    sections: DiscountSection[];
    expressionValue: string;
    expressionTitle: string;
    loadParameters: () => ExpressionParameter[];
    onSave: (expression: string | null, value: number | null | undefined, applyToLine: boolean, selectedSectionKeys: string[]) => void;
    onClose: () => void;
    disabled?: boolean;
} & ReactModal.Props;

const DiscountSectionsModal: React.FC<Props> = ({
    sections, expressionValue, expressionTitle, loadParameters,
    onSave, onClose, disabled,
    ...props
}: Props) => {
    const { t, i18n } = useTranslation();

    const [parameters] = useState<ExpressionParameter[]>(() => loadParameters());
    const [expression, setExpression] = useState(() => ExpressionSystem.translateFormula(expressionValue, i18n.language));
    const [result, setResult] = useState<ExpressionResult | null>(() => ExpressionSystem.evaluate(expressionValue, loadParameters()));
    const [applyToLine, setApplyToLine] = useState(true);
    const [selectedKeys, setSelectedKeys] = useState<string[]>(() =>
        sections.filter(s => s.isEligibleForDiscount || s.hasDiscountApplied).map(s => s.key)
    );
    const [showFunctionRef, setShowFunctionRef] = useState(false);
    const inputRef = useRef<ExpressionInputRef>(null);

    const EXPRESSION_STORAGE_KEY = 'expression';

    const toggleSection = (key: string) => {
        setSelectedKeys(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const handleExpressionChange = (expr: string | null) => {
        const newExpression = expr ?? '';
        setExpression(newExpression);
        setResult(ExpressionSystem.evaluate(newExpression, parameters));
    };

    const copyExpression = () => {
        const normalized = ExpressionSystem.normalizeToEnglish(expression);
        Storage.set(EXPRESSION_STORAGE_KEY, normalized);
        const nameMap: Record<string, string> = {};
        for (const p of parameters) {
            const serializedId = ExpressionSystem.serializeParameterMentionId(p.id);
            nameMap[serializedId] = p.name;
        }
        Storage.set(EXPRESSION_STORAGE_KEY + '_names', JSON.stringify(nameMap));
        navigator.clipboard.writeText(normalized);
    };

    const pasteExpression = () => {
        const pasteExpr = Storage.get(EXPRESSION_STORAGE_KEY);
        if (pasteExpr) {
            const translated = ExpressionSystem.translateFormula(pasteExpr, i18n.language);
            const nameMapJson = Storage.get(EXPRESSION_STORAGE_KEY + '_names');
            const nameMap: Record<string, string> | undefined = nameMapJson ? JSON.parse(nameMapJson) : undefined;
            inputRef.current?.updateValue(translated, nameMap);
            setExpression(translated);
        }
    };

    const handleSave = () => {
        const normalized = ExpressionSystem.normalizeToEnglish(expression);
        onSave(normalized, result ? result.value : null, applyToLine, selectedKeys);
    };

    const canSave = applyToLine || selectedKeys.length > 0;

    return (
        <Modal {...props} style={{ content: { minWidth: 500 } }} onRequestClose={onClose}>
            <div>
                <ModalHeader>
                    <Header>{expressionTitle}</Header>
                </ModalHeader>
                <ModalBody style={{ overflow: 'visible' }}>
                    <div className={styles.formulaContent}>
                        <div className={styles.resultContainer}>
                            <Label className={styles.resultLabel}>{t('common.result')}:</Label>
                            {result?.error ? 'ERROR' : ExpressionSystem.numberToStr(result?.value) ?? '0'}
                        </div>
                        <ExpressionInput
                            ref={inputRef}
                            value={expression}
                            parameters={parameters}
                            onChange={handleExpressionChange}
                            disabled={disabled}
                        />
                        <div className={styles.formulaActions}>
                            <Button
                                fw
                                variant='third'
                                onClick={copyExpression}
                                data-tooltip-id={BODY_TOOLTIP}
                                data-tooltip-content={t('common.copy')}
                            >
                                <IoCopyOutline />
                            </Button>
                            {!disabled && <Button
                                fw
                                variant='third'
                                onClick={pasteExpression}
                                data-tooltip-id={BODY_TOOLTIP}
                                data-tooltip-content={t('common.paste')}
                            >
                                <FaPaste />
                            </Button>}
                            {!disabled && <Button
                                fw
                                variant='third'
                                onClick={() => setShowFunctionRef(true)}
                                data-tooltip-id={BODY_TOOLTIP}
                                data-tooltip-content={t('common.function_reference')}
                            >
                                <FaBook />
                            </Button>}
                        </div>
                    </div>

                    <div className={styles.applySection}>
                        <Text className={styles.applyTitle}>
                            {t('budgets.budget.lines.header.discount_sections_modal_description')}
                        </Text>

                        <div className={styles.sectionsList}>
                            <div className={styles.sectionItem}>
                                <CheckInput
                                    checked={applyToLine}
                                    onChange={(e) => setApplyToLine(e.target.checked)}
                                    disabled={disabled}
                                >
                                    {t('budgets.budget.lines.header.discount_sections_modal_apply_to_line')}
                                </CheckInput>
                            </div>
                            {sections.map(section => (
                                <div key={section.key} className={styles.sectionItem}>
                                    <CheckInput
                                        checked={selectedKeys.includes(section.key)}
                                        onChange={() => toggleSection(section.key)}
                                        disabled={disabled}
                                    >
                                        {section.designation}
                                    </CheckInput>
                                    {section.hasInconsistentFormulas && (
                                        <span
                                            data-tooltip-id={BODY_TOOLTIP}
                                            data-tooltip-content={t('budgets.budget.lines.header.discount_sections_modal_inconsistent')}
                                        >
                                            <FaInfoCircle className={styles.warningIcon} />
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter className="text-right">
                    <Button fw variant='secondary' onClick={onClose}>{t('common.cancel')}</Button>
                    {!disabled && (
                        <Button onClick={handleSave} disabled={!canSave}>
                            {t('common.save')}
                        </Button>
                    )}
                </ModalFooter>
                <FunctionReferenceModal
                    isOpen={showFunctionRef}
                    onClose={() => setShowFunctionRef(false)}
                    onSelect={(functionName) => {
                        inputRef.current?.insertTextAtCursor(functionName + '(');
                    }}
                />
            </div>
        </Modal>
    );
};

export default DiscountSectionsModal;
